import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Chip, Divider, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import dayjs from 'dayjs';
import axios from '../axios';
import { getURL } from 'src/utils/config';
import { fDateTime } from 'src/utils/formatTime';
//import TestVideoPlayer from './TestVideoPlayer';
import PlaybackVideoPlayer from 'src/pages/PlaybackVideoPlayer';
import AuditVideoPlayer from 'src/sections/@dashboard/audit/AuditVideoPlayer';
import Logo from '../components/Logo';

const SESSION_TOKEN_KEY = (token) => `shared_access_token_${token}`;

const STEPS = ['Checking authentication', 'Loading footage data', 'Preparing player'];

const ALL_ZOOM_LEVELS = [
  { m: 15, label: '15m' },
  { m: 30, label: '30m' },
  { m: 60, label: '1h' },
  { m: 360, label: '6h' },
  { m: 720, label: '12h' },
];

const BLOCKED_COMMENT_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'img', 'svg', 'math']);

function renderCommentNode(node, key) {
  if (node.nodeType === 3) {
    return node.textContent.replace(/\u00a0/g, ' ');
  }

  if (node.nodeType !== 1) {
    return null;
  }

  const tagName = node.tagName.toLowerCase();
  if (BLOCKED_COMMENT_TAGS.has(tagName)) {
    return null;
  }

  const children = Array.from(node.childNodes).map((childNode, index) =>
    renderCommentNode(childNode, `${key}-${index}`)
  );

  switch (tagName) {
    case 'p':
      return <p key={key}>{children}</p>;
    case 'strong':
    case 'b':
      return <strong key={key}>{children}</strong>;
    case 'em':
    case 'i':
      return <em key={key}>{children}</em>;
    case 'ul':
      return <ul key={key}>{children}</ul>;
    case 'ol':
      return <ol key={key}>{children}</ol>;
    case 'li':
      return <li key={key}>{children}</li>;
    case 'br':
      return <br key={key} />;
    default:
      return <span key={key}>{children}</span>;
  }
}

function renderFormattedComment(comment = '') {
  const trimmedComment = String(comment || '').trim();
  if (!trimmedComment) {
    return null;
  }

  if (typeof DOMParser === 'undefined') {
    return trimmedComment.replace(/<[^>]*>/g, '');
  }

  const document = new DOMParser().parseFromString(trimmedComment, 'text/html');
  return Array.from(document.body.childNodes).map((node, index) =>
    renderCommentNode(node, `comment-${index}`)
  );
}

function buildSharedUrl(url, fromTimestamp, durationSecs) {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('from_ts', fromTimestamp);
    parsed.searchParams.set('duration', durationSecs);
    parsed.searchParams.set('clock_tolerance', 0);
    return parsed.toString();
  } catch {
    return url;
  }
}

function getUrlFromTimestamp(url) {
  try {
    const fromTimestamp = Number(new URL(url).searchParams.get('from_ts'));
    return Number.isFinite(fromTimestamp) && fromTimestamp > 0 ? fromTimestamp : null;
  } catch {
    return null;
  }
}

function EscalationPlayer({ footage }) {
  const event = footage?.events?.[0] ?? {};

  const sourceUrl =
    event.video_url ||
    footage?.recordings?.[0]?.local_play_url ||
    footage?.recordings?.[0]?.play_url ||
    null;

  const infoContent = (
    <Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>
        EVENT DETAILS
      </Typography>
      {[
        { label: 'Camera', value: event.devicename },
        { label: 'Event No', value: event.event_no },
        { label: 'Type', value: event.event_type },
        { label: 'Detected', value: event.detected_type },
        { label: 'Time', value: event.event_time ? fDateTime(event.event_time) : null },
      ].filter((r) => r.value).map(({ label, value }) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0, mr: 1 }}>{label}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.primary', textAlign: 'right' }}>{value}</Typography>
        </Box>
      ))}

      {event.comment && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>
            COMMENT
          </Typography>
          <Box
            sx={{
              color: 'text.primary',
              fontSize: 12,
              lineHeight: 1.6,
              textAlign: 'left',
              whiteSpace: 'normal',
              '& p': { margin: '0 0 6px' },
              '& p:last-child': { marginBottom: 0 },
              '& ul, & ol': { margin: '2px 0 10px', paddingLeft: '18px' },
              '& li': { margin: '2px 0' },
              '& strong, & b': { fontWeight: 700 },
            }}
          >
            {renderFormattedComment(event.comment)}
          </Box>
        </>
      )}

      {event.detected_type && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>
            DETECTED OBJECTS
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {event.detected_type.split(',').map((v, i) => (
              <Chip key={i} size="small" label={v.trim()} sx={{ textTransform: 'capitalize' }} />
            ))}
          </Box>
        </>
      )}

      {(event.audio_fie || event.audio_file) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>
            AUDIO ANNOUNCEMENTS
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{event.audio_type}</Typography>
              {event.no_of_times_announced > 0 && (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>×{event.no_of_times_announced}</Typography>
              )}
            </Box>
            <audio
              controls
              src={event.audio_fie || event.audio_file}
              style={{ width: '100%', height: 32 }}
            />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <AuditVideoPlayer
        sourceUrl={sourceUrl}
        cameraLabel={event.devicename || footage?.devicename || 'Shared Event'}
        cameraId={footage?.deviceid ?? null}
        eventNo={event.event_no ?? null}
        fromTime={event.event_time ?? null}
        toTime={event.event_time ? new Date(new Date(event.event_time).getTime() + 60000).toISOString() : null}
        infoContent={infoContent}
        disableShare
      />
    </Box>
  );
}

export default function SharedFootage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadStep, setLoadStep] = useState(0);
  const [doneSteps, setDoneSteps] = useState([]);
  const [error, setError] = useState('');
  const [footage, setFootage] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid share link.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        // Step 0: Check / get auth token
        setLoadStep(0);
        let accessToken = sessionStorage.getItem(SESSION_TOKEN_KEY(token));

        if (!accessToken) {
          const formData = new URLSearchParams();
          formData.append('username', 'vidisky_shared_acess');
          formData.append('password', token);
          formData.append('grant_type', 'shared_access');

          const tokenRes = await axios.post(getURL('token'), formData, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          });
          accessToken = tokenRes.data?.access_token;
          if (!accessToken) throw new Error('No access token returned.');
          sessionStorage.setItem(SESSION_TOKEN_KEY(token), accessToken);
        }
        setDoneSteps([0]);

        // Step 1: Fetch footage data
        setLoadStep(1);
        const recordingsRes = await axios.get(
          getURL(`recordings/recordings-by-shared-token?shared_token=${token}`),
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setDoneSteps([0, 1]);

        // Step 2: Prepare player
        setLoadStep(2);
        setFootage({ ...recordingsRes.data, accessToken });
        setDoneSteps([0, 1, 2]);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(detail || 'This link is invalid or has expired.');
        sessionStorage.removeItem(SESSION_TOKEN_KEY(token));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  if (loading || error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: 'background.default' }}>
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          width: 340, p: 4, borderRadius: 3,
          border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
          gap: 2.5,
        }}>
          <Logo sx={{ width: 130 }} />
          <Divider sx={{ width: '100%' }} />

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
              {STEPS.map((label, i) => {
                const done = doneSteps.includes(i);
                const active = loadStep === i && !done;
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {done
                        ? <CheckCircleRoundedIcon sx={{ fontSize: 20, color: 'success.main' }} />
                        : active
                          ? <CircularProgress size={16} thickness={4} />
                          : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'action.disabled', mx: 'auto' }} />
                      }
                    </Box>
                    <Typography sx={{ fontSize: 13, color: done ? 'text.primary' : active ? 'text.primary' : 'text.disabled', fontWeight: active ? 600 : 400 }}>
                      {label}{active ? '…' : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LinkOffRoundedIcon sx={{ fontSize: 28, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: 'text.primary' }}>
                Shared link unavailable
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.6 }}>
                This shared link has expired or is no longer valid. Please request a new link from the sender.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Escalation type → AuditVideoPlayer
  if (footage?.shared_link_type === 'escalation') {
    return <EscalationPlayer footage={footage} />;
  }

  // Archives type → existing TestVideoPlayer
  const fromTime = dayjs(footage?.details?.from_time);
  const toTime = dayjs(footage?.details?.to_time);
  const sharedDurationSecs = Math.max(1, toTime.diff(fromTime, 'second'));
  const sharedDurationMins = Math.max(1, toTime.diff(fromTime, 'minute', true));

  const baseRecording = (footage?.recordings ?? []).find((r) => {
    const rStart = dayjs(r.recording_start_time);
    const rEnd = dayjs(r.recording_end_time);
    return !fromTime.isBefore(rStart) && !fromTime.isAfter(rEnd);
  }) ?? footage?.recordings?.[0];

  const recordingStartTime = dayjs(baseRecording?.recording_start_time);
  const recordingFromTs =
    getUrlFromTimestamp(baseRecording?.local_play_url) ??
    getUrlFromTimestamp(baseRecording?.play_url);
  const fromTs =
    recordingFromTs && recordingStartTime.isValid()
      ? recordingFromTs + fromTime.diff(recordingStartTime, 'second')
      : fromTime.unix();
  const sharedClip = baseRecording
    ? {
        ...baseRecording,
        recording_start_time: fromTime.toISOString(),
        recording_end_time: toTime.toISOString(),
        play_url: buildSharedUrl(baseRecording.play_url, fromTs, sharedDurationSecs),
        local_play_url: buildSharedUrl(baseRecording.local_play_url, fromTs, sharedDurationSecs),
      }
    : null;

  const sourceUrl = sharedClip?.play_url || null;

  const availableZoomLevels = ALL_ZOOM_LEVELS.filter(({ m }) => m <= Math.max(15, sharedDurationMins * 4));

  return (
    <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <PlaybackVideoPlayer
        embedded
        sourceUrl={sourceUrl}
        currentClip={sharedClip}
        cameraLabel={footage?.devicename || 'Shared Footage'}
        cameraId={footage?.deviceid}
        timelineData={sharedClip ? [sharedClip] : []}
        hasPrevChunk={false}
        hasNextChunk={false}
        sharedMode
        accelerateOnTimelineHold
        availableZoomLevels={availableZoomLevels}
      />
    </Box>
  );
}
